import TableSkeleton from '../../../../components/common/TableSkeleton';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import CustomPagination from '../../../../components/common/CustomPagination';
import type { InvoiceFilterValues } from '../../../../components/Billing/Invoice/InvoiceFilter';
import style from '../Advance.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdvanceRequestItem, AdvanceRequestListResponse, getAdvanceRequestPatientList } from '../advance.helper';
import { getRequest } from '../../../../services/httpService';
import { useAppQuery } from '../../../../services/appQuery';
import { convertDateToDDMMYYYY, storePrevRoute } from '../../../../utils/commonUtils';

export interface AdvanceRequestListProps {
  search: string;
  filters: InvoiceFilterValues;
  patientId?: number;
}
const formatMoney = (val: number) => val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdvanceRequestList: React.FC<AdvanceRequestListProps> = ({ search, filters, patientId }) => {
  const { t:tBilling } = useTranslation('billing_config');
  const [page, setPage] = useState(0);
  const location = useLocation();
  const [pageSize, setPageSize] = useState(10);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

  
  const { data: AdvanceRequestList } = useAppQuery<AdvanceRequestListResponse>(
    [
      'AdvanceRequestList',
      page,
      pageSize,
      search,
      filters.fromDate,
      filters.toDate,
      patientId
    ],
    () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      if (search) params.set('search', search);
      if (filters.fromDate)
        params.set('checked_in_date_after', filters.fromDate);
      if (filters.toDate)
        params.set('checked_in_date_before', filters.toDate);
      if (patientId) params.set('patient_id', String(patientId));
      return getRequest<AdvanceRequestListResponse>(`${getAdvanceRequestPatientList()}?${params.toString()}`);
    },
    { staleTime: 0,
      gcTime: 0,
      refetchOnMount: true
    }
  );


  const navigate = useNavigate();
  
  const collectAdvance = (row: AdvanceRequestItem) => {
    storePrevRoute(location.pathname, location.search);
    navigate(`/admin/billing/advance/collect-advance/${row.patient_id}`, { state: { summary: row } });
  };
  return (
    <Box data-testid="advance-request-list-container">
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="advance-request-table-container">
        <Table aria-label={tBilling('Advance.aria.advance_request_table', { defaultValue: 'Advance Request list' })} data-testid="advance-request-table">
          <TableHead>
            <TableRow sx={{bgcolor:'#FAFAFA'}}>
              <TableCell className={style.tableHeader}>{tBilling('Advance.date')}</TableCell>
              {!patientId && (
                <TableCell className={style.tableHeader}>{tBilling('Advance.patient')}</TableCell>
              )}
              <TableCell className={style.tableHeader}>{tBilling('Advance.package_name')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.package_net_amount')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.package_paid_amount')}</TableCell>
              <TableCell className={style.tableHeader} >{tBilling('Advance.package_balance_amount')}</TableCell>
              <TableCell className={style.tableHeader}>{tBilling('Advance.remark')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Skeleton loader when loading */}
            {(!AdvanceRequestList) && (
              <TableSkeleton
                columnWidths={patientId ? [120, 220, 140, 140, 140, 100, 120] : [120, 200, 220, 140, 140, 140, 100, 120]}
                rows={10}
              />
            )}
            {/* Actual data rows */}
            {AdvanceRequestList && AdvanceRequestList?.objects?.map((row, idx) => (
              <TableRow key={idx} hover>
                <TableCell className={style.tableCells} sx={{ width: 160 }}>{convertDateToDDMMYYYY(row.date)}</TableCell>
                {!patientId && (
                  <TableCell className={style.tableCells} sx={{ minWidth: 260 }}>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" className={style.tableCells} sx={{ fontWeight: 600 }}>
                        {row.patient_name} | {row.age}
                      </Typography>
                      <Typography variant="caption" className={style.tableCells}   color="text.secondary">
                        {row.mrn_number} | {row.gender}
                      </Typography>
                    </Stack>
                  </TableCell>
                )}
                <TableCell className={style.tableCells} sx={{ minWidth: 240 }}>{row.package_name}</TableCell>
                <TableCell className={style.tableCells} >{formatMoney(row.package_net_amount ?? 0)}</TableCell>
                <TableCell className={style.tableCells} >{formatMoney(row.package_paid_amount ?? 0)}</TableCell>
                <TableCell className={style.tableCells} >{formatMoney(row.package_net_amount && row.package_paid_amount ? row.package_net_amount - row.package_paid_amount : 0)}</TableCell>
                <TableCell className={style.tableCells} sx={{ minWidth: 140 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" noWrap>{row.remark || '-'}</Typography>
                  </Stack>
                </TableCell>
                <TableCell  sx={{ width: 180 }}>
                  {(() => {
                    const isDisabled = row.status === 'COMPLETED' || row.visit_id === null;
                    const disabledReason = row.status === 'COMPLETED'
                      ? tBilling('Advance.messages.advance_already_collected', { defaultValue: 'Advance already collected.' })
                      : row.visit_id === null
                        ? tBilling('Advance.messages.no_visit_available_for_today', { defaultValue: 'No visit available for today to collect advance.' })
                        : '';
                    return (
                      <Tooltip title={disabledReason} arrow disableHoverListener={!isDisabled}>
                        <span style={{ display: 'inline-block' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={isDisabled}
                            sx={{ borderRadius: 3 }}
                            onClick={()=> collectAdvance(row)}
                          >
                            {tBilling('Advance.collect_advances')}
                          </Button>
                        </span>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
            {AdvanceRequestList?.objects?.length === 0 && (
              <TableRow>
                <TableCell colSpan={patientId ? 7 : 8}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    No entries found.
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>


      <CustomPagination
        page={page}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 15, 25, 50]}
        totalCount={AdvanceRequestList?.meta?.total_count || 0}
        onPageChange={(_, newPage) => setPage(newPage)}
      />
    </Box>
  );
};

export default AdvanceRequestList;
